import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

// Construct Component
function Confirmation({ scriptId, nextStep, currentSubscription, orderInfo }) {
  const handleStart = () => {
    if (typeof nextStep === 'function') {
      nextStep();
    }
  };

  // A Pro user switching billing frequency (e.g. Annual -> Monthly) isn't a
  // brand-new subscription, so the "Welcome to Pro" screen below is misleading —
  // Stripe schedules the change for the next renewal without charging now.
  const rawPlan = String(currentSubscription?.plan || '').toLowerCase();
  const rawType = String(currentSubscription?.type || '').toLowerCase();
  const isExistingPro =
    (rawPlan === 'pro' || rawType.includes('pro')) && !currentSubscription?.end_date;
  const previousFrequency = currentSubscription?.frequency;
  const newFrequency = orderInfo?.selectedPlan?.details?.frequency;
  const isFrequencySwitch =
    isExistingPro &&
    previousFrequency &&
    newFrequency &&
    previousFrequency !== newFrequency;

  if (isFrequencySwitch) {
    const expiresOn = fmtDate(currentSubscription?.next_recurring || currentSubscription?.end_date);

    return (
      <div className="modalContent">
        <div
          style={{
            padding: '32px 40px 28px',
            textAlign: 'center',
            maxWidth: 520,
            margin: '0 auto',
          }}
          id="successful_plan_switch"
        >
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: '#7557E8',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'white', fontSize: 56 }} />
          </div>

          <h2 className="purpleText" style={{ margin: '0 0 12px', fontSize: 26 }}>
            You&apos;ve switched to a {newFrequency} subscription
          </h2>

          <p style={{ fontSize: 18, margin: '0 0 8px', color: '#555', lineHeight: 1.5 }}>
            {previousFrequency === 'Yearly'
              ? `When your Annual plan expires${
                  expiresOn ? ` on ${expiresOn}` : ''
                }, it will renew at the ${newFrequency.toLowerCase()} rate.`
              : `Your plan will switch to ${newFrequency} billing at your next renewal${
                  expiresOn ? ` on ${expiresOn}` : ''
                }.`}
          </p>

          <p style={{ fontSize: 14, margin: '0 0 24px', color: '#888' }}>
            Your card has not been charged — Pro features remain active without interruption.
          </p>

          <button
            type="button"
            onClick={handleStart}
            className="buttonClone"
            style={{ width: '300px' }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modalContent">
      <div
        style={{
          padding: '32px 40px 28px',
          textAlign: 'center',
          maxWidth: 520,
          margin: '0 auto',
        }}
        id="successful_new_subscription"
      >
        {/* Trophy circle */}
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: '#7557E8', // purple
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FontAwesomeIcon icon={faTrophy} style={{ color: 'white', fontSize: 56 }} />
        </div>

        {/* Title with centered sparkles */}
        <h2
          className="purpleText"
          style={{
            margin: '0 0 12px',
            fontSize: 26,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>✨</span>
          <span>Welcome to Pro!</span>
          <span style={{ fontSize: 18, lineHeight: 1 }}>✨</span>
        </h2>

        {/* Subtitle */}
        <p
          style={{
            fontSize: 18,
            margin: '0 0 24px',
            color: '#555',
            lineHeight: 1.5,
          }}
        >
          Congratulations! You&apos;ve unlocked the full power of ShotKraft Pro.
        </p>

        <hr style={{ margin: '10px 0 24px', borderColor: '#eee', opacity: 0.8 }} />

        {/* Section header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 12,
            gap: 8,
            color: '#444',
            fontWeight: 600,
          }}
        >
          <span style={{ fontSize: 16 }}>⚡</span>
          <span>Your Pro membership includes:</span>
        </div>

        {/* Feature list */}
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0 0 16px',
            textAlign: 'left',
            maxWidth: 420,
            marginInline: 'auto',
          }}
        >
          <li style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
            <span className="purpleText">✔</span>
            <span>Generate shots for up to 3 scripts per month</span>
          </li>
          <li style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
            <span className="purpleText">✔</span>
            <span>More Fields: Multi-Cam, VFX, etc.</span>
          </li>
          <li style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
            <span className="purpleText">✔</span>
            <span>Flexible Export Options, including CSV</span>
          </li>
          <li style={{ display: 'flex', gap: 8 }}>
            <span className="purpleText">✔</span>
            <span>Priority support</span>
          </li>
        </ul>

        {scriptId ? (
          <div
            style={{
              marginTop: 10,
              marginBottom: 18,
              fontSize: 14,
              color: '#666',
            }}
          >
            This script has been upgraded to a full-featured script as well.
          </div>
        ) : null}

        {/* CTA button */}
        <button
          type="button"
          onClick={handleStart}
          className="buttonClone"
          style={{ width: '300px' }}
        >
          Start Creating
        </button>

        {/* Footer note */}
        <div
          style={{
            marginTop: 16,
            fontSize: 13,
            color: '#888',
          }}
        >
          Pro is live! Convert old projects from the Projects page anytime.
        </div>
      </div>
    </div>
  );
}

export default Confirmation;
